import { useLocation } from "@tanstack/react-router";
import {
	forwardRef,
	type ReactNode,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { LobuMark } from "@/components/brand/lobu-mark";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrgContext } from "@/hooks/use-org-context";
import { lazyWithReload } from "@/lib/lazy-with-reload";
import type { LobuRefKind } from "@/lib/references";
import {
	type AssistApi,
	AssistContext,
	type AssistSeed,
	resolveAssistContent,
	seedForRoute,
	useAssistInternal,
} from "./assist-context";

// Dynamic import, deliberately: the Ask pill mounts on every workspace page,
// but Tiptap is only needed once the popover opens. A static import pushed the
// entry chunk to 2.55MB, over the 2.5MB browser budget; lazyWithReload also
// recovers a chunk a newer deploy removed.
const AssistComposer = lazyWithReload(async () => ({
	default: (await import("./assist-composer")).AssistComposer,
}));

function isEditableTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	return (
		target.isContentEditable ||
		target.tagName === "INPUT" ||
		target.tagName === "TEXTAREA" ||
		target.tagName === "SELECT"
	);
}

/** The page's own objects lead its @-picker; every other kind stays available. */
function preferredMentionKindsForSeed(seed: AssistSeed): LobuRefKind[] {
	switch (seed.section) {
		case "automations":
			return ["automation"];
		case "connectors":
			return ["connection", "connector"];
		case "context":
			return ["entity"];
		default:
			return [];
	}
}

/**
 * Shared ask state and popover, opened by the AssistDock pill or ⌘J (which
 * toggles it from any workspace route). The composer starts a new
 * conversation on send.
 */
export function AssistProvider({ children }: { children: ReactNode }) {
	const [seed, setSeed] = useState<AssistSeed | null>(null);
	const { currentOwner } = useOrgContext();
	const { pathname } = useLocation();
	const popoverRef = useRef<HTMLDivElement>(null);
	// Where focus was when the popover opened, and whether the close should
	// hand it back (Escape, ⌘J, send) rather than leave it where the user
	// clicked or navigated.
	const returnFocusRef = useRef<HTMLElement | null>(null);
	const restoreFocusOnCloseRef = useRef(false);

	const rememberFocus = useCallback(() => {
		const active = document.activeElement;
		returnFocusRef.current = active instanceof HTMLElement ? active : null;
	}, []);
	const open = useCallback(
		(next: AssistSeed) => {
			rememberFocus();
			setSeed(next);
		},
		[rememberFocus],
	);
	const close = useCallback(() => {
		restoreFocusOnCloseRef.current = true;
		setSeed(null);
	}, []);
	const isOpen = seed !== null;

	// Hand focus back after the close has rendered. The pill unmounts while
	// the popover is open, so the remembered element may be gone: fall back to
	// the freshly mounted pill.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (wasOpenRef.current && !isOpen && restoreFocusOnCloseRef.current) {
			restoreFocusOnCloseRef.current = false;
			const target = returnFocusRef.current;
			if (target?.isConnected) target.focus();
			else document.querySelector<HTMLElement>("[data-assist-pill]")?.focus();
		}
		wasOpenRef.current = isOpen;
	}, [isOpen]);

	// The seed is bound to the route and workspace it was opened on: drop it on
	// navigation (history, links) so a stale draft cannot be sent to another
	// workspace's agent.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname and currentOwner are the triggers
	useEffect(() => {
		setSeed(null);
	}, [pathname, currentOwner]);

	useEffect(() => {
		if (!isOpen) return;
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node | null;
			// The @-mention menu mounts on body, outside the popover. Selecting a
			// reference must insert the chip, not dismiss the Ask dialog.
			if (target instanceof Element && target.closest("[data-mention-popup]")) {
				return;
			}
			if (target && !popoverRef.current?.contains(target)) {
				setSeed(null);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [isOpen, close]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (
				(event.key !== "j" && event.key !== "J") ||
				!(event.metaKey || event.ctrlKey) ||
				(!isOpen && isEditableTarget(event.target))
			) {
				return;
			}
			event.preventDefault();
			if (isOpen) {
				close();
				return;
			}
			if (!currentOwner || currentOwner.startsWith("@")) return;
			rememberFocus();
			setSeed(seedForRoute(currentOwner, pathname));
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [currentOwner, pathname, isOpen, close, rememberFocus]);

	const api = useMemo<AssistApi>(
		() => ({ open, close, isOpen }),
		[open, close, isOpen],
	);

	const owner =
		currentOwner && !currentOwner.startsWith("@") ? currentOwner : null;

	return (
		<AssistContext.Provider value={api}>
			{children}
			{isOpen && seed && owner ? (
				<AssistPopover
					ref={popoverRef}
					owner={owner}
					seed={seed}
					onClose={close}
				/>
			) : null}
		</AssistContext.Provider>
	);
}

// Floats over the page's bottom center, like the popover it opens, rather
// than taking a row of the page's height.
export function AssistDock() {
	const { currentOwner } = useOrgContext();
	const { pathname } = useLocation();
	const { isOpen } = useAssistInternal();
	const owner =
		currentOwner && !currentOwner.startsWith("@") ? currentOwner : null;
	// A chat thread has its own composer. Segment-exact: `/acme/chat-messages`
	// is an entity type, not the chat surface.
	const firstSegment =
		owner && pathname.startsWith(`/${owner}/`)
			? pathname.slice(owner.length + 2).split("/")[0]
			: "";
	// Personal account pages do not describe the selected workspace's Home.
	if (!owner || firstSegment === "chat" || pathname.startsWith("/account/"))
		return null;

	if (isOpen) return null;
	return (
		<div
			style={{
				position: "fixed",
				bottom: "calc(1rem + env(safe-area-inset-bottom))",
				left: "50%",
				transform: "translateX(-50%)",
				marginLeft: "var(--assist-shift, 0px)",
				zIndex: 40,
				maxWidth: "calc(100vw - 2rem - 2 * var(--assist-shift, 0px))",
			}}
		>
			<AssistPill owner={owner} pathname={pathname} />
		</div>
	);
}

function AssistPill({ owner, pathname }: { owner: string; pathname: string }) {
	const { open } = useAssistInternal();
	return (
		<button
			type="button"
			data-assist-pill
			onClick={() => open(seedForRoute(owner, pathname))}
			aria-label="Ask (⌘J)"
			title="Ask (⌘J)"
			className="flex max-w-full items-center gap-2 rounded-full border bg-popover px-4 py-2 text-sm font-medium text-popover-foreground shadow-lg transition-colors hover:bg-accent"
		>
			<LobuMark className="h-4 w-4 shrink-0" />
			<span className="max-w-55 overflow-hidden text-ellipsis whitespace-nowrap">
				Ask
			</span>
			<span className="shrink-0 text-xs text-muted-foreground">⌘J</span>
		</button>
	);
}

const EDITOR_SELECTOR = "textarea, [contenteditable='true']";

const AssistPopover = forwardRef<
	HTMLDivElement,
	{ owner: string; seed: AssistSeed; onClose: () => void }
>(function AssistPopover({ owner, seed, onClose }, ref) {
	const content = useMemo(() => resolveAssistContent(seed), [seed]);
	const innerRef = useRef<HTMLDivElement | null>(null);
	const setRefs = useCallback(
		(node: HTMLDivElement | null) => {
			innerRef.current = node;
			if (typeof ref === "function") ref(node);
			else if (ref) ref.current = node;
		},
		[ref],
	);
	// Focus the editor on open. The composer can render its editor a moment
	// later (agents still loading), so wait for it to appear.
	useEffect(() => {
		const root = innerRef.current;
		if (!root) return;
		const focusEditor = () => {
			const editor = root.querySelector<HTMLElement>(EDITOR_SELECTOR);
			editor?.focus();
			return editor != null;
		};
		if (focusEditor()) return;
		// No composer (no agents yet): move focus into the dialog so keyboard
		// users land on the setup links instead of <body>.
		const firstFocusable = root.querySelector<HTMLElement>("a[href], button");
		(firstFocusable ?? root).focus();
		const observer = new MutationObserver(() => {
			if (focusEditor()) observer.disconnect();
		});
		observer.observe(root, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, []);
	return (
		<div
			ref={setRefs}
			tabIndex={-1}
			role="dialog"
			aria-label="Ask"
			style={{
				position: "fixed",
				bottom: 16,
				left: "50%",
				transform: "translateX(-50%)",
				marginLeft: "var(--assist-shift, 0px)",
				zIndex: 50,
				// Size against the content column, not the viewport: the popover is
				// shifted right by half the sidebar width, so subtract the shift on
				// both sides to keep Send inside the window.
				width: "min(48rem, calc(100vw - 2rem - 2 * var(--assist-shift, 0px)))",
				maxHeight: "calc(100dvh - 2rem)",
			}}
			className="overflow-y-auto"
		>
			<Suspense fallback={<Skeleton className="h-32 w-full rounded-lg" />}>
				<AssistComposer
					key={`${seed.owner}:${seed.section}:${seed.entityTypeName ?? ""}:${seed.filterSummary ?? ""}`}
					ownerSlug={owner}
					starters={content.starters}
					preferredMentionKinds={preferredMentionKindsForSeed(seed)}
					onSent={onClose}
				/>
			</Suspense>
		</div>
	);
});
